import type { AdoptResult, ReportSetup } from "@crux/contracts";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Setting up a report: what a model has proposed for it so far.
 *
 * Every call is safe to repeat. The server keeps what it was asked and answers
 * a repeat from that, so the wizard can ask again whenever it needs the answer
 * rather than remembering whether it already paid for it.
 */

export async function fetchReportSetup(
  workspaceId: string,
  reportId: string,
): Promise<ReportSetup> {
  const response = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/setup",
    { params: { path: { workspaceId, reportId } } },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "open that brand's setup"));
  }
  return response.data;
}

export async function suggestSetupCompetitors(
  workspaceId: string,
  reportId: string,
): Promise<ReportSetup> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/reports/{reportId}/setup/competitors",
    { params: { path: { workspaceId, reportId } } },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "find that brand's competitors"));
  }
  return response.data;
}

/** "unavailable" when no model is configured, so the questions are typed in. */
export async function writeSetupPrompts(
  workspaceId: string,
  reportId: string,
  describes: string,
): Promise<ReportSetup | "unavailable"> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/reports/{reportId}/setup/prompts",
    { params: { path: { workspaceId, reportId } }, body: { describes } },
  );
  if (response.data === undefined) {
    if (response.response.status === 501) return "unavailable";
    // A 400 here says what is missing ("we do not know what this brand sells
    // yet"); a bare status left a customer pressing Next three times.
    if (response.response.status === 400) throw new Error(response.error.message);
    throw new Error(problem(response.response.status, "suggest questions"));
  }
  return response.data;
}

export async function finishReportSetup(
  workspaceId: string,
  reportId: string,
  texts: readonly string[],
): Promise<AdoptResult> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/reports/{reportId}/setup/finish",
    {
      params: { path: { workspaceId, reportId } },
      body: { texts: [...texts] },
    },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "finish this report setup"));
  }
  return response.data;
}

export async function discardReportSetup(
  workspaceId: string,
  reportId: string,
): Promise<{ brandDeleted: boolean }> {
  const response = await api.DELETE(
    "/api/workspaces/{workspaceId}/reports/{reportId}/setup",
    { params: { path: { workspaceId, reportId } } },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "discard this unfinished setup"));
  }
  return response.data;
}
